/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  actualTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // Initialize theme from localStorage or default to 'system'
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme') as Theme;
      return stored || 'system';
    }
    return 'system';
  });

  // Determine the actual theme based on system preference
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    // Set initial system theme
    setSystemTheme(mediaQuery.matches ? 'dark' : 'light');

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const actualTheme = theme === 'system' ? systemTheme : theme;

  useEffect(() => {
    const root = window.document.documentElement;

    // Remove previous theme classes
    root.classList.remove('light', 'dark');

    // Add current theme class
    root.classList.add(actualTheme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        actualTheme === 'dark' ? '#1f2937' : '#ffffff'
      );
    }
  }, [actualTheme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const toggleTheme = () => {
    setTheme(actualTheme === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    actualTheme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

// Theme-aware CSS classes utility
export const themeClasses = {
  // Background classes
  bg: {
    primary: 'bg-white dark:bg-slate-800 dark:bg-slate-900',
    secondary: 'bg-gray-50 dark:bg-slate-900 dark:bg-slate-800',
    tertiary: 'bg-gray-100 dark:bg-slate-800 dark:bg-slate-700',
    elevated: 'bg-white dark:bg-slate-800',
    card: 'bg-white dark:bg-slate-800',
    hover: 'hover:bg-gray-50 dark:hover:bg-slate-800 dark:hover:bg-slate-700',
    active: 'bg-blue-50 dark:bg-blue-900/20',
  },

  // Text classes
  text: {
    primary: 'text-gray-900 dark:text-slate-100',
    secondary: 'text-gray-600 dark:text-slate-300',
    tertiary: 'text-gray-500 dark:text-slate-400',
    muted: 'text-gray-400 dark:text-slate-500',
    inverse: 'text-white dark:text-slate-900',
    accent: 'text-blue-600 dark:text-blue-400',
    success: 'text-green-600 dark:text-green-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    error: 'text-red-600 dark:text-red-400',
  },

  // Border classes
  border: {
    primary: 'border-gray-200 dark:border-slate-700',
    secondary: 'border-gray-300 dark:border-slate-600',
    focus: 'border-blue-500 dark:border-blue-400',
    hover: 'hover:border-gray-300 dark:hover:border-slate-600',
  },

  // Ring/shadow classes
  ring: {
    focus: 'ring-blue-500 dark:ring-blue-400',
    error: 'ring-red-500 dark:ring-red-400',
  },

  shadow: {
    sm: 'shadow-sm dark:shadow-gray-900/20',
    md: 'shadow-md dark:shadow-gray-900/20',
    lg: 'shadow-lg dark:shadow-gray-900/25',
    xl: 'shadow-xl dark:shadow-gray-900/25',
  },

  // Component-specific classes
  input:
    'bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-400 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400',

  button: {
    primary:
      'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white',
    secondary:
      'bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-900 dark:text-slate-100',
    ghost:
      'hover:bg-gray-100 dark:hover:bg-slate-700 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-300',
  },

  card: 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-sm dark:shadow-gray-900/20',

  modal:
    'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-2xl dark:shadow-gray-900/50',

  dropdown:
    'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg dark:shadow-gray-900/25',
};

// Utility function to combine theme classes
export const cn = (...classes: (string | undefined | boolean)[]) => {
  return classes.filter(Boolean).join(' ');
};
